/**
 * Member role in a persistent room.
 */
export type RoomRole = 'admin' | 'member';

/**
 * Supported room scene identifiers used by VR clients.
 */
export type RoomSceneId = 'in_the_trees' | 'sci_fi_office' | 'headquarters';

/**
 * Default room scene used when no scene is set.
 */
export const DEFAULT_ROOM_SCENE_ID: RoomSceneId = 'sci_fi_office';

/**
 * Labels for each supported room scene.
 */
export const ROOM_SCENE_LABELS: Record<RoomSceneId, string> = {
  in_the_trees: 'In The Trees',
  sci_fi_office: 'Star Lounge',
  headquarters: 'Headquarters',
};

/**
 * A member of a persistent room.
 */
export interface RoomMember {
  userId: string;
  email: string;
  displayName?: string;
  role: RoomRole;
  joinedAt: Date;
}

/**
 * Persistent room data stored in Firestore.
 * The room ID is used as the LiveKit room name.
 */
export interface PersistentRoom {
  id: string;
  name: string;
  imageUrl: string | null;
  sceneId: RoomSceneId;
  createdBy: string;
  createdAt: Date;
  members: RoomMember[];
  adminUserIds?: string[];
}

/**
 * Room activity log entry.
 */
export interface RoomLogEntry {
  id: string;
  userId: string;
  userName: string;
  action: 'entered' | 'left';
  timestamp: Date;
}

/**
 * Room presence - who's currently in a room.
 */
export interface RoomPresence {
  userId: string;
  userName: string;
  joinedAt: Date;
}

/**
 * Data for creating a new persistent room.
 */
export interface CreateRoomInput {
  name: string;
  imageFile?: File;
  sceneId?: RoomSceneId;
}

/**
 * Data for updating a persistent room.
 */
export interface UpdateRoomInput {
  name?: string;
  imageFile?: File;
  sceneId?: RoomSceneId;
}

/**
 * Data for inviting a member to a room.
 */
export interface InviteMemberInput {
  email: string;
  role: RoomRole;
}
